import { ReactNode } from "react";

import Refresh from "@mui/icons-material/Refresh";
import { CircularProgress, IconButton, Box } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";

import { getCurrentAccountBalance } from "../../store/modules/accountBalance/accountBalanceActions";
import { loadPastStatementInformation } from "../../store/modules/pastCharges/pastChargesActions";
import { RootState } from "../../store/store";
import { formatCurrency } from "../../utils/helpers";
import { useIsMaguireEnabled } from "../../utils/maguireHelper";

const PAST_CHARGES_PAGE_SIZE = 5;
const PAST_CHARGES_PAGE_NUMBER = 1;

/** Renders a spinner in place of the value while its data is loading */
function BalanceValue({ isLoading, children }: { isLoading: boolean; children: ReactNode }) {
  return isLoading ? <CircularProgress size={20} /> : <>{children}</>;
}

export default function Balances() {
  const isMaguireEnabled = useIsMaguireEnabled();
  const dispatch = useDispatch();
  const customerAccountId = useSelector((state: RootState) => state.customer.customer?.customerAccountId);
  const balance = useSelector((state: RootState) => state.balance.balanceState.payload);
  const charges = useSelector((state: RootState) => state.dueCharges.dueChargesState.dueCharges);

  const currentBalance = useSelector((state: RootState) => state.balance.currentBalanceState.accountBalance);
  const iscurrentBalanceLoading = useSelector((state: RootState) => state.balance.currentBalanceState.isLoading);
  const currentCharges = useSelector(
    (state: RootState) => state.pastCharges.statementInformationState.statementInformation
  );
  const iscurrentChargesLoading = useSelector(
    (state: RootState) => state.pastCharges.statementInformationState.isLoading
  );

  const handleRefreshBalance = () => {
    if (!customerAccountId) return;
    getCurrentAccountBalance(dispatch, customerAccountId);
    loadPastStatementInformation(dispatch, customerAccountId, PAST_CHARGES_PAGE_SIZE, PAST_CHARGES_PAGE_NUMBER);
  };

  return isMaguireEnabled ? (
    <Grid container direction="row" spacing={2} sx={{ padding: 1, alignItems: "baseline" }}>
      <Grid item xs={4}>
        <Typography variant="h6" fontWeight="bold">
          LIVE BALANCE
        </Typography>
        <Typography variant="caption">(includes transactions post Current Statement)</Typography>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" component="div" sx={{ fontSize: "15px", marginLeft: 1 }}>
            <BalanceValue isLoading={iscurrentBalanceLoading}>{formatCurrency(currentBalance ?? 0)}</BalanceValue>
          </Typography>
          <IconButton
            size="small"
            onClick={handleRefreshBalance}
            disabled={iscurrentBalanceLoading}
            aria-label="refresh balance"
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Box>
      </Grid>
      {/* empty column keeps CURRENT STATEMENT aligned with the rest of this row's grid */}
      <Grid item xs={4} />
      <Grid item xs>
        <Typography variant="h6" fontWeight="bold">
          CURRENT STATEMENT
        </Typography>
        <Grid container direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
          <Grid item>
            <Typography variant="caption" sx={{ fontSize: "15px" }}>
              AMOUNT DUE
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography variant="h6" component="div" sx={{ fontSize: "15px" }}>
                <BalanceValue isLoading={iscurrentChargesLoading}>
                  {formatCurrency(currentCharges[0]?.currentDueAmount ?? 0)}
                </BalanceValue>
              </Typography>
              <IconButton
                size="small"
                onClick={handleRefreshBalance}
                disabled={iscurrentChargesLoading}
                aria-label="refresh charges"
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
          <Divider orientation="vertical" flexItem sx={{ height: "100%", marginX: 1 }} />
          <Grid item>
            <Typography variant="caption" sx={{ fontSize: "15px" }}>
              DUE DATE
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography variant="h6" component="div" sx={{ fontSize: "15px" }}>
                <BalanceValue isLoading={iscurrentChargesLoading}>
                  {currentCharges[0]?.dueDate ? dayjs(currentCharges[0].dueDate).format("DD MMM YYYY") : "-"}
                </BalanceValue>
              </Typography>
              {/* invisible button matches AMOUNT DUE's height so both values line up */}
              <IconButton size="small" sx={{ visibility: "hidden" }} aria-hidden tabIndex={-1}>
                <Refresh fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  ) : (
    <Grid container direction="row" spacing={1}>
      <Grid item xs={4}></Grid>
      <Grid item xs={4} />
      <Grid item xs>
        <strong className={`color-accent-2 small`}>CURRENT BALANCE</strong>
        <br />
        <Typography variant="h5">{balance ? formatCurrency(balance?.balance) : "$0.00"}</Typography>
      </Grid>
      <Divider orientation="vertical" variant="middle" flexItem />
      <Grid item>
        <strong className={`color-accent-2 small`}>YOUR NEXT BILL</strong>
        <br />
        {charges && charges.length > 0 ? (
          <>
            <Typography variant="h5">{formatCurrency(charges[0].balance)}</Typography>
            <strong className={`color-accent-2 small`}>
              {charges[0].isOverDue ? "NOW" : dayjs(charges[0].dueDate).format("ddd DD")}
            </strong>
          </>
        ) : (
          <Typography variant="h5">{formatCurrency(0)}</Typography>
        )}
      </Grid>
    </Grid>
  );
}
